import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationDocumentTypeCoverage } from '../models/Company/types'

const QUERY_ROOT = ['organizationDocumentTypeCoverage'] as const

export interface OrganizationDocumentTypeCoverageParams {
    organization: number | null
    includeInactive?: boolean
    documentTypeId?: number | null
    enabled?: boolean
}

const buildCoverageQueryString = (
    params: OrganizationDocumentTypeCoverageParams,
) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    if (params.documentTypeId != null) {
        searchParams.append('document_type', String(params.documentTypeId))
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationDocumentTypeCoverage = (
    params: OrganizationDocumentTypeCoverageParams,
) => {
    const queryString = buildCoverageQueryString(params)

    const { data: coverage, isLoading, error, refetch } = useQuery<
        OrganizationDocumentTypeCoverage[],
        Error
    >({
        queryKey: [
            ...QUERY_ROOT,
            params.organization,
            params.includeInactive,
            params.documentTypeId,
        ],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_document_type/coverage/${queryString}`,
            )
            return normalizeListResponse<OrganizationDocumentTypeCoverage>(
                res.data,
            )
        },
        enabled: !!params.organization && params.enabled !== false,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    return {
        coverage: coverage ?? [],
        isLoading,
        error,
        refetch,
    }
}
